import { useState, useEffect, useCallback, useMemo, Fragment, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, ChevronDown, TrendingUp, Plug, ClipboardList } from 'lucide-react'
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, ReferenceLine, LabelList,
} from 'recharts'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useAccountNicknames } from '../hooks/useAccountNicknames'
import { parseOCC, isOCCSymbol, formatOCC } from '../lib/occParser'
import { ibkrQuoteStream, selectPositionQuotePrice } from '../lib/marketData/ibkrQuoteSubscriptionStore.js'
import { getMarketSession } from '../lib/marketSession'
import AccountName from '../components/AccountName'
import { usePrivacy } from '../hooks/usePrivacy'
import { usePositionProjectLookup } from '../hooks/usePositionProjectLookup'
import { syncPositionProjects } from '../lib/syncPositionProjects'
import { derivePositionPnl } from '../lib/broker/normalizedPnl'
import { deriveBrokerSnapshotAvailability } from '../lib/broker/brokerSnapshotAvailability'
import { computePositionDayPnl, getContractMultiplier } from '../lib/portfolioAnalytics'
import { buildPositionWorkspaceTarget } from '../lib/positionWorkspaceRoute'

// ── Column Configuration ──────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'symbol', label: 'Symbol', align: 'left', width: '120px' },
  { key: 'quantity', label: 'Qty', align: 'right', width: '60px' },
  { key: 'price', label: 'Price', align: 'right', width: '85px' },
  { key: 'priceChange', label: 'Price Chg', align: 'right', width: '85px' },
  { key: 'marketValue', label: 'Mkt Value', align: 'right', width: '100px' },
  { key: 'dayChange', label: 'Day Chg', align: 'right', width: '100px' },
  { key: 'costBasis', label: 'Cost Basis', align: 'right', width: '95px' },
  { key: 'gainLoss', label: 'Gain/Loss', align: 'right', width: '100px' },
  { key: 'stop', label: 'Stop', align: 'right', width: '80px' },
  { key: 'stopPct', label: 'Stop %', align: 'right', width: '65px' },
  { key: 'riskDollars', label: 'Risk $', align: 'right', width: '85px' },
  { key: 'riskPct', label: 'Risk %', align: 'right', width: '65px' },
  { key: 'pctHoldings', label: '% Holdings', align: 'right', width: '80px' },
]

// ── Instrument Group Definitions ──────────────────────────────────────────────
const INSTRUMENT_GROUPS = [
  { key: 'equities', label: 'Equities', types: ['EQUITY'] },
  { key: 'etfs', label: 'ETFs & Funds', types: ['COLLECTIVE_INVESTMENT', 'MUTUAL_FUND'] },
  { key: 'options', label: 'Options', types: ['OPTION'] },
  { key: 'fixedIncome', label: 'Fixed Income', types: ['FIXED_INCOME'] },
  { key: 'cash', label: 'Cash & Money Market', types: ['CASH_EQUIVALENT', 'CURRENCY'] },
]

// ── Chart Color Palette ───────────────────────────────────────────────────────
const CHART_COLORS = [
  '#4a6741', '#6aaa5e', '#5b7ea1', '#c4953a', '#7c6bb5',
  '#4a9c8c', '#d4764e', '#b83a2e', '#e05a4a', '#8a92a0',
]

// ── Sector Classification Map ─────────────────────────────────────────────────
// Maps ticker symbols to GICS sectors. Options use the underlying symbol.
// ETFs map to their primary sector exposure; broad-market ETFs → "Broad Market".
const SECTOR_MAP = {
  // Technology
  AAPL: 'Technology', MSFT: 'Technology', NVDA: 'Technology', AMD: 'Technology',
  GOOGL: 'Technology', GOOG: 'Technology', META: 'Technology', AVGO: 'Technology',
  CRM: 'Technology', ADBE: 'Technology', INTC: 'Technology', CSCO: 'Technology',
  ORCL: 'Technology', NOW: 'Technology', UBER: 'Technology', SHOP: 'Technology',
  SQ: 'Technology', PLTR: 'Technology', CRWD: 'Technology', SNOW: 'Technology',
  NET: 'Technology', DDOG: 'Technology', MDB: 'Technology', COIN: 'Technology',
  SOFI: 'Technology', SNAP: 'Technology', PINS: 'Technology', RBLX: 'Technology',
  // Consumer Discretionary
  AMZN: 'Consumer Discretionary', TSLA: 'Consumer Discretionary',
  HD: 'Consumer Discretionary', NKE: 'Consumer Discretionary',
  SBUX: 'Consumer Discretionary', TGT: 'Consumer Discretionary',
  LOW: 'Consumer Discretionary', BKNG: 'Consumer Discretionary',
  ABNB: 'Consumer Discretionary', DIS: 'Consumer Discretionary',
  NFLX: 'Consumer Discretionary',
  // Financials
  JPM: 'Financials', BAC: 'Financials', WFC: 'Financials', GS: 'Financials',
  MS: 'Financials', V: 'Financials', MA: 'Financials', AXP: 'Financials',
  BLK: 'Financials', SCHW: 'Financials', C: 'Financials',
  // Healthcare
  UNH: 'Healthcare', JNJ: 'Healthcare', LLY: 'Healthcare', PFE: 'Healthcare',
  ABBV: 'Healthcare', MRK: 'Healthcare', TMO: 'Healthcare', ABT: 'Healthcare',
  // Industrials
  BA: 'Industrials', CAT: 'Industrials', HON: 'Industrials', UPS: 'Industrials',
  GE: 'Industrials', RTX: 'Industrials', LMT: 'Industrials', DE: 'Industrials',
  // Energy
  XOM: 'Energy', CVX: 'Energy', COP: 'Energy', SLB: 'Energy',
  EOG: 'Energy', OXY: 'Energy',
  // Consumer Staples
  PG: 'Consumer Staples', KO: 'Consumer Staples', PEP: 'Consumer Staples',
  COST: 'Consumer Staples', WMT: 'Consumer Staples', PM: 'Consumer Staples',
  // Communication Services
  T: 'Communication', VZ: 'Communication', TMUS: 'Communication',
  // Real Estate
  AMT: 'Real Estate', PLD: 'Real Estate', CCI: 'Real Estate',
  // Materials
  LIN: 'Materials', APD: 'Materials', FCX: 'Materials',
  // Utilities
  NEE: 'Utilities', DUK: 'Utilities', SO: 'Utilities',
  // ETFs — sector
  XLF: 'Financials', XLE: 'Energy', XLK: 'Technology', XLV: 'Healthcare',
  XLI: 'Industrials', XLP: 'Consumer Staples', XLY: 'Consumer Discretionary',
  XLU: 'Utilities', XLB: 'Materials', XLRE: 'Real Estate',
  // ETFs — broad market
  SPY: 'Broad Market', QQQ: 'Broad Market', IWM: 'Broad Market',
  DIA: 'Broad Market', VOO: 'Broad Market', VTI: 'Broad Market',
  // ETFs — other
  GLD: 'Commodities', SLV: 'Commodities', USO: 'Commodities',
  TLT: 'Fixed Income', AGG: 'Fixed Income', HYG: 'Fixed Income',
}

/** Extract the underlying ticker from an OCC option symbol or plain symbol */
function getUnderlying(symbol) {
  const trimmed = symbol.trim()
  // OCC format: 6-char root + YYMMDD + C/P + 8-digit strike (21 chars total)
  if (trimmed.length >= 15 && /\d{6}[CP]\d{8}$/.test(trimmed)) {
    return trimmed.slice(0, 6).trim()
  }
  return trimmed
}

function getSector(symbol) {
  return SECTOR_MAP[getUnderlying(symbol)] || 'Other'
}

/** Returns true if symbol is in OCC option format (e.g. "TSLA  260515P00370000") */
function isOptionSymbol(symbol) {
  if (!symbol) return false
  const trimmed = symbol.trim()
  return trimmed.length >= 15 && /\d{6}[CP]\d{8}$/.test(trimmed)
}

// ── Formatters ────────────────────────────────────────────────────────────────
const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})

function fmtCurrency(val) {
  if (val == null) return '—'
  return currencyFmt.format(Number(val))
}

function fmtCompact(val) {
  if (val == null) return '—'
  const n = Number(val)
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 100_000) return `$${(n / 1_000).toFixed(1)}k`
  return fmtCurrency(n)
}

function fmtSigned(val) {
  if (val == null) return '—'
  const n = Number(val)
  return (n >= 0 ? '+' : '') + fmtCurrency(n)
}

function fmtPct(val) {
  if (val == null || !isFinite(val)) return '—'
  return (val >= 0 ? '+' : '') + val.toFixed(2) + '%'
}

function fmtQty(val) {
  if (val == null) return '—'
  const n = Number(val)
  if (n === Math.floor(n)) return n.toLocaleString()
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 })
}

function clr(val) {
  if (val > 0) return 'text-forest'
  if (val < 0) return 'text-ember'
  return 'text-inkMuted'
}

// ── Data Processing ───────────────────────────────────────────────────────────
function buildGroups(positions) {
  const bySymbol = {}

  for (const pos of positions) {
    if (!bySymbol[pos.symbol]) {
      bySymbol[pos.symbol] = {
        symbol: pos.symbol,
        description: pos.raw?.instrument?.description || '',
        instrumentType: pos.instrument_type,
        contractMultiplier: getContractMultiplier(pos),
        accounts: [],
        totalQty: 0, totalMktVal: 0, totalDayPnl: 0, totalCostBasis: 0, totalGainLoss: 0,
      }
    }

    const c = bySymbol[pos.symbol]
    const qty = Number(pos.quantity) || 0
    const mktVal = Number(pos.market_value) || 0
    const avgCost = Number(pos.average_price) || 0
    // EF-156: Single source of truth for cost basis + G/L. Reads normalized row.
    const { costBasis, gainLoss, gainLossPct } = derivePositionPnl(pos)
    const prevClose = Number(pos.prev_close_price) || 0
    const dayPnl = computePositionDayPnl(pos)

    c.accounts.push({
      accountNumber: pos.account_number, qty, mktVal, dayPnl, avgCost, costBasis, gainLoss,
      gainLossPct,
      prevClosePrice: prevClose,
      // EF-156: propagate normalized fields so liveAccounts can route through the helper.
      cost_basis_long: pos.cost_basis_long,
      cost_basis_short: pos.cost_basis_short,
      unrealized_pnl_long: pos.unrealized_pnl_long,
      unrealized_pnl_short: pos.unrealized_pnl_short,
      cost_basis_source: pos.cost_basis_source,
      currency: pos.currency,
      contract_multiplier: pos.contract_multiplier,
      average_price: pos.average_price,
      instrument_type: pos.instrument_type,
      market_value: mktVal,
      quantity: qty,
    })

    c.totalQty += qty
    c.totalMktVal += mktVal
    c.totalDayPnl += dayPnl
    c.totalCostBasis += costBasis
    c.totalGainLoss += gainLoss
  }

  const consolidated = Object.values(bySymbol).map(c => {
    c.accounts.sort((a, b) => b.mktVal - a.mktVal)
    const prevMktVal = c.totalMktVal - c.totalDayPnl
    const gainLoss = c.totalGainLoss
    return {
      ...c,
      price: c.totalQty > 0 ? c.totalMktVal / (c.totalQty * c.contractMultiplier) : 0,
      priceChange: c.totalQty > 0 ? c.totalDayPnl / (c.totalQty * c.contractMultiplier) : 0,
      priceChangePct: prevMktVal > 0 ? (c.totalDayPnl / prevMktVal) * 100 : 0,
      dayChangePct: prevMktVal > 0 ? (c.totalDayPnl / prevMktVal) * 100 : 0,
      gainLoss,
      gainLossPct: c.totalCostBasis > 0 ? (gainLoss / c.totalCostBasis) * 100 : 0,
      isMulti: c.accounts.length > 1,
    }
  })

  const groups = []
  const used = new Set()

  for (const def of INSTRUMENT_GROUPS) {
    const items = consolidated
      .filter(c => def.types.includes(c.instrumentType))
      .sort((a, b) => b.totalMktVal - a.totalMktVal)
    if (!items.length) continue
    items.forEach(c => used.add(c.symbol))

    const totMkt = items.reduce((s, c) => s + c.totalMktVal, 0)
    const totDay = items.reduce((s, c) => s + c.totalDayPnl, 0)
    const totCost = items.reduce((s, c) => s + c.totalCostBasis, 0)
    const totGL = items.reduce((s, c) => s + c.gainLoss, 0)
    const prevTot = totMkt - totDay

    groups.push({
      ...def, positions: items,
      totalMktVal: totMkt, totalDayPnl: totDay, totalCostBasis: totCost, totalGainLoss: totGL,
      dayChangePct: prevTot > 0 ? (totDay / prevTot) * 100 : 0,
      gainLossPct: totCost > 0 ? (totGL / totCost) * 100 : 0,
    })
  }

  const others = consolidated.filter(c => !used.has(c.symbol)).sort((a, b) => b.totalMktVal - a.totalMktVal)
  if (others.length) {
    const totMkt = others.reduce((s, c) => s + c.totalMktVal, 0)
    const totDay = others.reduce((s, c) => s + c.totalDayPnl, 0)
    const totCost = others.reduce((s, c) => s + c.totalCostBasis, 0)
    const totGL = others.reduce((s, c) => s + c.gainLoss, 0)
    const prevTot = totMkt - totDay
    groups.push({
      key: 'other', label: 'Other', positions: others,
      totalMktVal: totMkt, totalDayPnl: totDay, totalCostBasis: totCost, totalGainLoss: totGL,
      dayChangePct: prevTot > 0 ? (totDay / prevTot) * 100 : 0,
      gainLossPct: totCost > 0 ? (totGL / totCost) * 100 : 0,
    })
  }

  return groups
}

// ── Chart Sub-components ──────────────────────────────────────────────────────
function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-sans text-xs font-semibold text-ink">{d.name}</div>
      <div className="font-mono text-xs text-inkMuted">
        {fmtCurrency(d.value)}{d.pct != null ? ` (${d.pct.toFixed(1)}%)` : ''}
      </div>
    </div>
  )
}

function DonutChart({ data, centerLabel, centerSub, size = 200 }) {
  if (!data.length) return null
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%" cy="50%"
            innerRadius={size * 0.28}
            outerRadius={size * 0.42}
            paddingAngle={1}
            dataKey="value"
            stroke="none"
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="font-mono text-sm font-bold text-ink">{centerLabel}</div>
        {centerSub && <div className="font-mono text-[10px] text-inkMuted">{centerSub}</div>}
      </div>
    </div>
  )
}

function ChartBreakdownTable({ headers, rows }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border">
          {headers.map((h, i) => (
            <th key={i} className={`py-2 pr-3 font-mono text-[10px] tracking-widest uppercase text-inkMuted ${i === 0 ? 'text-left' : 'text-right'}`}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className="border-b border-border last:border-0">
            {row.map((cell, ci) => (
              <td key={ci} className={`py-2 pr-3 font-mono text-xs ${ci === 0 ? 'text-left text-ink' : 'text-right text-inkMuted'}`}>
                <div className="flex items-center gap-1.5">
                  {ci === 0 && row._color && (
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: row._color }} />
                  )}
                  {cell}
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PositionsCurrent() {
  const { user } = useAuth()
  const { getNickname } = useAccountNicknames()
  const { privacyMode } = usePrivacy()
  const navigate = useNavigate()
  const { projectsBySymbol, reload: reloadProjects } = usePositionProjectLookup()
  const mask = (val) => privacyMode ? '•••••' : val
  const [positions, setPositions] = useState([])
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [connected, setConnected] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [collapsedGroups, setCollapsedGroups] = useState(new Set())
  const [collapsedSymbols, setCollapsedSymbols] = useState(new Set())
  const [collapsedOrderAccounts, setCollapsedOrderAccounts] = useState(new Set())
  const [selectedAccounts, setSelectedAccounts] = useState(new Set()) // empty = all
  const [orders, setOrders] = useState([])
  const [ordersError, setOrdersError] = useState(null)
  const [currentPrices, setCurrentPrices] = useState({})
  const [livePrices, setLivePrices] = useState({}) // { symbol: { last, close, bid, ask, ... } }
  const ordersFetched = useRef(false)
  const stickyHeaderRef = useRef(null)

  // Extended hours session detection (60s poll)
  const [marketSession, setMarketSession] = useState(() => getMarketSession())
  useEffect(() => {
    const interval = setInterval(() => {
      const s = getMarketSession()
      setMarketSession(prev => prev !== s ? s : prev)
    }, 60000)
    return () => clearInterval(interval)
  }, [])
  const isExtendedSession = marketSession === 'pre' || marketSession === 'post'

  // ── Data Loading ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user) return
    if (positions.length === 0) setLoading(true)

    const [tokenResult, positionResult, accountResult] = await Promise.all([
      supabase.from('broker_tokens').select('broker')
        .eq('user_id', user.id).eq('broker', 'schwab').maybeSingle(),
      supabase.from('schwab_positions')
        .select('account_number, symbol, instrument_type, quantity, average_price, market_value, current_day_pnl, prev_close_price, raw, status, cost_basis_long, cost_basis_short, unrealized_pnl_long, unrealized_pnl_short, cost_basis_source, currency, contract_multiplier, last_updated_at')
        .eq('user_id', user.id)
        .or('status.eq.open,status.is.null')
        .order('market_value', { ascending: false }),
      supabase.from('schwab_accounts')
        .select('*')
        .eq('user_id', user.id).order('account_number'),
    ])

    const availability = deriveBrokerSnapshotAvailability({
      tokenRow: tokenResult.data,
      tokenError: tokenResult.error,
      positionRows: positionResult.data,
      positionError: positionResult.error,
    })
    setConnected(availability.connected)
    setLoadError(availability.loadError)
    setPositions(positionResult.data || [])
    setAccounts(accountResult.data || [])
    setLoading(false)
  }, [user])

  useEffect(() => { loadData() }, [loadData])

  // ── Live IBKR tick stream for ALL positions via WebSocket ────────────────
  useEffect(() => {
    if (!positions.length) return
    const unsubs = []
    const uniqueSymbols = [...new Set(positions.map(p => p.symbol).filter(Boolean))]

    for (const sym of uniqueSymbols) {
      const isOpt = isOCCSymbol(sym)
      const occ = isOpt ? parseOCC(sym) : null
      const streamSymbol = occ ? occ.underlying : sym
      const params = occ
        ? { secType: 'OPT', expiry: occ.expiry, strike: String(occ.strike), right: occ.right }
        : {}

      ibkrQuoteStream.subscribe(streamSymbol, (tick) => {
        if (tick.field === 'reset') {
          setLivePrices(prev => ({ ...prev, [sym]: {} }))
          return
        }
        // Accumulate tick fields into per-symbol quote object
        setLivePrices(prev => {
          const existing = prev[sym] || {}
          return { ...prev, [sym]: {
            ...existing,
            [tick.field]: tick.value,
            ...(tick.session ? { session: tick.session } : {}),
            ...(tick.contract ? { contract: tick.contract } : {}),
          } }
        })
      }, params).then(fn => unsubs.push(fn)).catch(() => {})
    }

    return () => unsubs.forEach(fn => fn())
  }, [positions])

  // ── Fetch Open Orders ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!user || ordersFetched.current) return
    ordersFetched.current = true
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return
        const res = await fetch('/api/schwab/orders', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        if (res.ok) {
          const json = await res.json()
          setOrders(json.orders || [])
        } else {
          setOrdersError('Failed to load orders')
        }
      } catch {
        setOrdersError('Failed to load orders')
      }
    })()
  }, [user])

  // ── Account Filtering ─────────────────────────────────────────────────────
  const isAll = selectedAccounts.size === 0

  const toggleAccount = (num) => {
    setSelectedAccounts(prev => {
      if (prev.size === 0) return new Set([num])
      const next = new Set(prev)
      if (next.has(num)) {
        next.delete(num)
        return next.size === 0 ? new Set() : next
      } else {
        next.add(num)
        return next.size === accounts.length ? new Set() : next
      }
    })
  }

  const filteredPositions = useMemo(
    () => isAll ? positions : positions.filter(p => selectedAccounts.has(p.account_number)),
    [positions, selectedAccounts, isAll]
  )

  const filteredAccounts = useMemo(
    () => isAll ? accounts : accounts.filter(a => selectedAccounts.has(a.account_number)),
    [accounts, selectedAccounts, isAll]
  )

  // ── Derived Data ──────────────────────────────────────────────────────────
  const groups = useMemo(() => buildGroups(filteredPositions), [filteredPositions])

  // Apply live IBKR prices → full cascade: price → mktVal → P&L → gain/loss → day P&L
  const liveGroups = useMemo(() => {
    if (Object.keys(livePrices).length === 0) return groups
    return groups.map(g => {
      const livePositions = g.positions.map(pos => {
        const lp = livePrices[pos.symbol]
        if (!lp) return pos
        // Until the broker's short market-value sign is proven for every adapter,
        // keep its authoritative short P&L instead of deriving an inverted value.
        if (pos.totalQty <= 0) return pos
        const isOpt = isOCCSymbol(pos.symbol)
        const secType = pos.instrumentType === 'FUTURE' ? 'FUT' : (isOpt ? 'OPT' : 'STK')
        const lastPrice = selectPositionQuotePrice(lp, lp.session || marketSession, secType)
        if (lastPrice == null) return pos

        const change = lp.close > 0 ? lastPrice - lp.close : null
        const changePct = lp.close > 0 ? ((lastPrice - lp.close) / lp.close) * 100 : null
        const multiplier = pos.contractMultiplier || 1
        const newMktVal = lastPrice * pos.totalQty * multiplier
        const newGainLoss = newMktVal - pos.totalCostBasis
        const newGLPct = pos.totalCostBasis > 0 ? (newGainLoss / pos.totalCostBasis) * 100 : 0
        const prevCloseVal = lp.close > 0 ? lp.close * pos.totalQty * multiplier : null
        const newDayPnl = prevCloseVal != null ? newMktVal - prevCloseVal : pos.totalDayPnl
        const prevDayMktVal = prevCloseVal ?? (newMktVal - pos.totalDayPnl)
        const newDayPct = prevDayMktVal > 0 ? (newDayPnl / prevDayMktVal) * 100 : 0

        const liveAccounts = pos.accounts ? pos.accounts.map(a => {
          if (a.qty <= 0) return a
          const aLiveMktVal = lastPrice * a.qty * multiplier
          // EF-156: Route through the helper with liveMktVal so the live-vs-stale
          // decision is made INSIDE the helper, not at the call site. The helper
          // bypasses broker_open_pnl when liveMktVal is supplied.
          const { costBasis: aCostBasis, gainLoss: aGainLoss, gainLossPct: aGainLossPct } =
            derivePositionPnl(a, { liveMktVal: aLiveMktVal })
          return {
            ...a,
            mktVal: aLiveMktVal,
            costBasis: aCostBasis,
            gainLoss: aGainLoss,
            gainLossPct: aGainLossPct,
            dayPnl: lp.close > 0 ? (lastPrice - lp.close) * a.qty * multiplier : a.dayPnl,
          }
        }) : pos.accounts

        return {
          ...pos, price: lastPrice, priceChange: change ?? pos.priceChange, priceChangePct: changePct ?? pos.priceChangePct,
          dayChangePct: newDayPct, totalMktVal: newMktVal, totalDayPnl: newDayPnl,
          gainLoss: newGainLoss, gainLossPct: newGLPct, accounts: liveAccounts,
        }
      })
      const gMktVal = livePositions.reduce((s, p) => s + p.totalMktVal, 0)
      const gDayPnl = livePositions.reduce((s, p) => s + p.totalDayPnl, 0)
      const gCostBasis = livePositions.reduce((s, p) => s + p.totalCostBasis, 0)
      const gGL = livePositions.reduce((s, p) => s + p.gainLoss, 0)
      const gPrev = gMktVal - gDayPnl
      return {
        ...g, positions: livePositions, totalMktVal: gMktVal, totalDayPnl: gDayPnl, totalCostBasis: gCostBasis,
        totalGainLoss: gGL, dayChangePct: gPrev > 0 ? (gDayPnl / gPrev) * 100 : 0, gainLossPct: gCostBasis > 0 ? (gGL / gCostBasis) * 100 : 0,
      }
    })
  }, [groups, livePrices, marketSession])

  const allConsolidated = useMemo(() => liveGroups.flatMap(g => g.positions), [liveGroups])

  const totalMktVal = liveGroups.reduce((s, g) => s + g.totalMktVal, 0)
  const totalDayPnl = liveGroups.reduce((s, g) => s + g.totalDayPnl, 0)
  const totalCostBasis = liveGroups.reduce((s, g) => s + g.totalCostBasis, 0)
  const totalGainLoss = totalMktVal - totalCostBasis
  const prevTotal = totalMktVal - totalDayPnl
  const totalDayPct = prevTotal > 0 ? (totalDayPnl / prevTotal) * 100 : 0
  const totalGLPct = totalCostBasis > 0 ? (totalGainLoss / totalCostBasis) * 100 : 0
  const colCount = COLUMNS.length

  // ── Color Maps (stable across filters) ────────────────────────────────────
  const symbolColorMap = useMemo(() => {
    const bySymbol = {}
    for (const p of positions) {
      bySymbol[p.symbol] = (bySymbol[p.symbol] || 0) + (Number(p.market_value) || 0)
    }
    const sorted = Object.entries(bySymbol).sort((a, b) => b[1] - a[1])
    const map = new Map()
    sorted.forEach(([sym], i) => map.set(sym, CHART_COLORS[i % CHART_COLORS.length]))
    return map
  }, [positions])

  const accountColorMap = useMemo(() => {
    const map = new Map()
    accounts.forEach((a, i) => map.set(a.account_number, CHART_COLORS[i % CHART_COLORS.length]))
    return map
  }, [accounts])

  // ── Cash & Margin Data ────────────────────────────────────────────────────
  const cashData = useMemo(() => {
    return filteredAccounts.map(a => {
      const eq = a.equity || {}
      const value = eq.liquidationValue ?? eq.totalAccountValue ?? 0
      const cash = eq.cashBalance ?? eq.availableFunds ?? 0
      const invested = value - cash
      const isIRA = (a.account_type || '').includes('IRA')
      return {
        accountNumber: a.account_number,
        accountType: a.account_type,
        isIRA,
        value, cash, invested,
        cashPct: value > 0 ? (cash / value) * 100 : 0,
        investedPct: value > 0 ? (invested / value) * 100 : 0,
        buyingPower: a.buying_power ?? eq.buyingPower ?? null,
        availableFunds: a.available_funds ?? eq.availableFunds ?? null,
        marginBalance: a.margin_balance ?? eq.marginBalance ?? null,
        maintenanceReq: a.maintenance_requirement ?? eq.maintenanceRequirement ?? null,
        dayTradingBP: a.day_trading_buying_power ?? eq.dayTradingBuyingPower ?? null,
      }
    })
  }, [filteredAccounts])

  // ── Filtered Orders ──────────────────────────────────────────────────────
  const filteredOrders = useMemo(() => {
    if (!orders.length) return []
    const filtered = isAll ? orders : orders.filter(o => selectedAccounts.has(o.accountNumber))
    return filtered
  }, [orders, selectedAccounts, isAll])

  // Find a position matching an order's symbol + account (option-aware: order may have underlying "TSLA" while position has OCC "TSLA  260515P00370000")
  const findMatchingPosition = useCallback((orderSymbol, accountNumber) => {
    // Exact match first (equities, ETFs)
    const exact = positions.find(p => p.symbol === orderSymbol && p.account_number === accountNumber)
    if (exact) return exact
    // Option prefix match: order symbol is the underlying, position symbol starts with it and is an option
    return positions.find(p =>
      p.instrument_type === 'OPTION' &&
      p.account_number === accountNumber &&
      p.symbol.startsWith(orderSymbol)
    ) || null
  }, [positions])

  // Orders for display in Open Orders section — excludes standalone position stops
  const displayOrders = useMemo(() => {
    return filteredOrders.filter(order => {
      const leg = order.orderLegCollection?.[0]
      if (!leg?.instrument?.symbol) return true
      const instruction = leg.instruction || ''
      const isSellStop = !order.hasChildren &&
        instruction.startsWith('SELL') &&
        (order.orderType === 'STOP' || order.orderType === 'STOP_LIMIT') &&
        order.stopPrice != null
      if (!isSellStop) return true
      // Exclude if it matches an open position (it's a position stop, shown in positions table)
      return !findMatchingPosition(leg.instrument.symbol, order.accountNumber)
    })
  }, [filteredOrders, findMatchingPosition])

  const ordersByAccount = useMemo(() => {
    const map = {}
    for (const order of displayOrders) {
      const acct = order.accountNumber
      if (!map[acct]) map[acct] = []
      map[acct].push(order)
    }
    // Sort within each account: bracket parents first, then standalone
    for (const acct of Object.keys(map)) {
      map[acct].sort((a, b) => (b.hasChildren ? 1 : 0) - (a.hasChildren ? 1 : 0))
    }
    return map
  }, [displayOrders])

  // ── Account Equity Map (for % Portfolio) ────────────────────────────────
  const accountEquityMap = useMemo(() => {
    const map = {}
    for (const a of accounts) {
      const eq = a.equity || {}
      map[a.account_number] = eq.equity ?? eq.liquidationValue ?? eq.totalAccountValue ?? 0
    }
    return map
  }, [accounts])

  // ── Stop Price Map (symbol+account → stopPrice from orders) ──────────────
  // Maps each position to its stop price from open orders
  // Key uses the POSITION's symbol (e.g. OCC for options), not the order's symbol
  const stopMap = useMemo(() => {
    const map = {}
    for (const order of orders) {
      const leg = order.orderLegCollection?.[0]
      if (!leg?.instrument?.symbol) continue
      const orderSym = leg.instrument.symbol
      const acct = order.accountNumber

      // Bracket order: use computedStopPrice from child
      if (order.hasChildren && order.computedStopPrice != null) {
        const key = `${orderSym}|${acct}`
        const curPrice = order.entryPrice ?? order.price ?? order.stopPrice ?? 0
        const existing = map[key]
        // Keep the stop closest to current price
        if (!existing || Math.abs(curPrice - order.computedStopPrice) < Math.abs(curPrice - existing.stopPrice)) {
          map[key] = { stopPrice: order.computedStopPrice }
        }
      }

      // Standalone SELL STOP/STOP_LIMIT (not a child): map to position
      const instruction = leg.instruction || ''
      if (
        !order.hasChildren &&
        instruction.startsWith('SELL') &&
        (order.orderType === 'STOP' || order.orderType === 'STOP_LIMIT') &&
        order.stopPrice != null
      ) {
        // Find the matching position to use its symbol as key (option-aware)
        const matchedPos = findMatchingPosition(orderSym, acct)
        const key = matchedPos ? `${matchedPos.symbol}|${acct}` : `${orderSym}|${acct}`
        if (!map[key]) {
          map[key] = { stopPrice: order.stopPrice }
        }
      }
    }
    return map
  }, [orders, findMatchingPosition])

  // Total equity for risk % on consolidated rows
  const totalEquity = useMemo(() => {
    const accts = isAll ? accounts : accounts.filter(a => selectedAccounts.has(a.account_number))
    return accts.reduce((s, a) => s + (accountEquityMap[a.account_number] || 0), 0)
  }, [accounts, accountEquityMap, isAll, selectedAccounts])

  const hasAnyMargin = cashData.some(r => !r.isIRA)

  const cashTotals = useMemo(() => {
    const val = cashData.reduce((s, c) => s + c.value, 0)
    const cash = cashData.reduce((s, c) => s + c.cash, 0)
    const invested = val - cash
    const marginRows = cashData.filter(r => !r.isIRA)
    const totalBP = marginRows.reduce((s, c) => s + (c.buyingPower || 0), 0)
    const totalAF = marginRows.reduce((s, c) => s + (c.availableFunds || 0), 0)
    const totalMR = marginRows.reduce((s, c) => s + (c.maintenanceReq || 0), 0)
    return {
      value: val, cash, invested,
      cashPct: val > 0 ? (cash / val) * 100 : 0,
      investedPct: val > 0 ? (invested / val) * 100 : 0,
      buyingPower: totalBP,
      availableFunds: totalAF,
      maintenanceReq: totalMR,
    }
  }, [cashData])

  // ── Portfolio Value = total account equity (positions + cash) ──────────
  const portfolioValue = cashTotals.value || totalMktVal

  // ── Chart Data ────────────────────────────────────────────────────────────
  const typeChartData = useMemo(() => {
    return liveGroups.map((g, i) => ({
      name: g.label,
      value: g.totalMktVal,
      pct: portfolioValue > 0 ? (g.totalMktVal / portfolioValue) * 100 : 0,
      count: g.positions.length,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }))
  }, [liveGroups, portfolioValue])

  const topHoldingsData = useMemo(() => {
    const sorted = [...allConsolidated].sort((a, b) => b.totalMktVal - a.totalMktVal)
    const top = sorted.slice(0, 8)
    const rest = sorted.slice(8)
    const restTotal = rest.reduce((s, p) => s + p.totalMktVal, 0)
    const data = top.map(p => ({
      name: p.symbol,
      value: p.totalMktVal,
      pct: portfolioValue > 0 ? (p.totalMktVal / portfolioValue) * 100 : 0,
      acctCount: p.accounts.length,
      color: symbolColorMap.get(p.symbol),
    }))
    if (restTotal > 0) {
      data.push({
        name: 'Other',
        value: restTotal,
        pct: portfolioValue > 0 ? (restTotal / portfolioValue) * 100 : 0,
        acctCount: new Set(rest.flatMap(p => p.accounts.map(a => a.accountNumber))).size,
        color: '#8a92a0',
      })
    }
    return data
  }, [allConsolidated, portfolioValue, symbolColorMap])

  const accountChartData = useMemo(() => {
    return filteredAccounts.map(a => {
      const eq = a.equity || {}
      const val = eq.liquidationValue ?? eq.totalAccountValue ?? 0
      const totalVal = cashTotals.value
      const posCount = filteredPositions.filter(p => p.account_number === a.account_number).length
      return {
        name: getNickname(a.account_number),
        value: val,
        pct: totalVal > 0 ? (val / totalVal) * 100 : 0,
        posCount,
        color: accountColorMap.get(a.account_number),
      }
    })
  }, [filteredAccounts, cashTotals.value, filteredPositions, getNickname, accountColorMap])

  const concentrationData = useMemo(() => {
    return [...allConsolidated]
      .sort((a, b) => b.totalMktVal - a.totalMktVal)
      .map(p => ({
        symbol: p.symbol,
        pct: portfolioValue > 0 ? (p.totalMktVal / portfolioValue) * 100 : 0,
        pctLabel: portfolioValue > 0 ? ((p.totalMktVal / portfolioValue) * 100).toFixed(1) + '%' : '0%',
      }))
  }, [allConsolidated, portfolioValue])

  const cashVsInvestedData = useMemo(() => {
    if (cashTotals.value === 0) return []
    return [
      { name: 'Cash', value: cashTotals.cash, pct: cashTotals.cashPct, color: '#5b7ea1' },
      { name: 'Invested', value: cashTotals.invested, pct: cashTotals.investedPct, color: '#4a6741' },
    ]
  }, [cashTotals])

  const sectorChartData = useMemo(() => {
    const bySector = {}
    for (const p of allConsolidated) {
      const sector = getSector(p.symbol)
      if (!bySector[sector]) bySector[sector] = { value: 0, count: 0 }
      bySector[sector].value += p.totalMktVal
      bySector[sector].count += 1
    }
    return Object.entries(bySector)
      .sort((a, b) => b[1].value - a[1].value)
      .map(([name, d], i) => ({
        name,
        value: d.value,
        pct: portfolioValue > 0 ? (d.value / portfolioValue) * 100 : 0,
        count: d.count,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }))
  }, [allConsolidated, portfolioValue])

  // ── Toggle Handlers ───────────────────────────────────────────────────────
  const toggleGroup = (key) => setCollapsedGroups(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const toggleSymbol = (sym) => setCollapsedSymbols(prev => {
    const next = new Set(prev)
    next.has(sym) ? next.delete(sym) : next.add(sym)
    return next
  })

  const toggleOrderAccount = (acct) => setCollapsedOrderAccounts(prev => {
    const next = new Set(prev)
    next.has(acct) ? next.delete(acct) : next.add(acct)
    return next
  })

  // ── Navigate to Position Workspace ──────────────────────────────────────
  const handlePositionClick = async (symbol, e) => {
    // Don't navigate if clicking the expand chevron for multi-account rows
    if (e?.defaultPrevented) return

    const existingId = projectsBySymbol[symbol]
    if (existingId) {
      navigate(buildPositionWorkspaceTarget({ symbol, projectId: existingId }))
      return
    }

    // Create on-the-fly: find matching open trade, create project, navigate
    if (!user) return
    const { data: trade } = await supabase
      .from('trades')
      .select('id, symbol, asset_type, account_number, avg_entry_price, stop_loss, quantity_total, entry_date')
      .eq('user_id', user.id)
      .eq('symbol', symbol)
      .in('status', ['open', 'partial'])
      .limit(1)
      .maybeSingle()

    const newProject = {
      user_id: user.id,
      symbol,
      asset_type: trade?.asset_type || 'equity',
      stage: 'active',
      entry_price: trade?.avg_entry_price || null,
      stop_price: trade?.stop_loss || null,
      trade_id: trade?.id || null,
      is_draft: false,
      activated_at: trade?.entry_date || new Date().toISOString(),
    }

    const { data: created } = await supabase
      .from('position_projects')
      .insert(newProject)
      .select('id')
      .single()

    if (created) {
      reloadProjects()
      navigate(buildPositionWorkspaceTarget({ symbol, projectId: created.id }))
    }
  }

  // ── Loading / Empty States ────────────────────────────────────────────────
  if (loading) return <div className="font-mono text-xs text-inkMuted animate-pulse">Loading positions…</div>

  if (loadError) return (
    <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
      <Plug size={24} className="text-border mx-auto mb-3" strokeWidth={1.5} />
      <div className="font-mono text-xs text-inkMuted mb-3">{loadError}</div>
      <button type="button" onClick={loadData} className="font-mono text-xs text-ink underline">
        Try again
      </button>
    </div>
  )

  if (!connected) return (
    <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
      <Plug size={24} className="text-border mx-auto mb-3" strokeWidth={1.5} />
      <div className="font-mono text-xs text-inkMuted mb-1">No broker connected</div>
      <Link to="/integrations" className="font-mono text-xs text-ink underline">Connect Schwab to sync positions →</Link>
    </div>
  )

  if (!positions.length) return (
    <div className="bg-canvas border border-border rounded-xl px-6 py-16 text-center">
      <TrendingUp size={24} className="text-border mx-auto mb-3" strokeWidth={1.5} />
      <div className="font-mono text-xs text-inkMuted mb-1">No positions synced yet</div>
      <Link to="/integrations" className="font-mono text-xs text-ink underline">Sync from Integrations →</Link>
    </div>
  )

  // ── Detect dark mode for chart axis colors ────────────────────────────────
  const isDark = document.documentElement.classList.contains('dark')
  const axisColor = isDark ? '#8a92a0' : '#3d3b34'
  const chartGreen = isDark ? '#6aaa5e' : '#4a6741'
  const chartEmber = isDark ? '#e05a4a' : '#b83a2e'

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {/* ── Sticky Page Header + Account Filter ────────────────────────────── */}
      <div
        ref={stickyHeaderRef}
        className="sticky z-30 -mx-4 md:-mx-8 px-4 md:px-8"
        style={{ top: 0, background: 'rgb(var(--color-parchment))' }}
      >
        <div className="flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Live from broker</div>
            <h1 className="font-black text-2xl uppercase tracking-wide text-ink">Positions</h1>
          </div>
          <div className="text-right">
            <div className="font-mono text-xl font-semibold text-ink tabular-nums whitespace-nowrap">{mask(fmtCompact(portfolioValue))}</div>
            <div className={`font-mono text-sm tabular-nums whitespace-nowrap ${isExtendedSession ? (totalDayPnl >= 0 ? 'text-blue-400' : 'text-violet-400') : clr(totalDayPnl)}`}>
              {mask(`${fmtSigned(totalDayPnl)} (${fmtPct(totalDayPct)})`)}
            </div>
          </div>
        </div>

        {accounts.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => setSelectedAccounts(new Set())}
              className={`px-3 py-1.5 rounded-lg font-mono text-xs transition-all ${
                isAll
                  ? 'bg-ink text-parchment'
                  : 'bg-canvas2 text-inkMuted border border-border hover:border-ink hover:text-ink'
              }`}
            >
              All Accounts
            </button>
            {accounts.map(acct => {
              const active = !isAll && selectedAccounts.has(acct.account_number)
              return (
                <button
                  key={acct.account_number}
                  onClick={() => toggleAccount(acct.account_number)}
                  className={`px-3 py-1.5 rounded-lg font-mono text-xs transition-all ${
                    active
                      ? 'bg-ink text-parchment'
                      : 'bg-canvas2 text-inkMuted border border-border hover:border-ink hover:text-ink'
                  }`}
                >
                  {getNickname(acct.account_number)}
                </button>
              )
            })}
          </div>
        )}

        {/* ── Column Headers (inside sticky block) ─────────────────────── */}
        <div className="border-x border-t border-border rounded-t-xl bg-canvas" style={{ marginTop: 12 }}>
          <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: '1120px', borderCollapse: 'separate', borderSpacing: 0 }}>
            <colgroup>
              {COLUMNS.map(col => <col key={col.key} style={{ width: col.width }} />)}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map(col => (
                  <th key={col.key} className={`px-4 py-3 ${col.align === 'left' ? 'text-left' : 'text-right'} font-mono text-[10px] tracking-widest uppercase text-inkMuted whitespace-nowrap`} style={{ background: 'rgb(var(--color-canvas))', boxShadow: '0 1px 0 rgb(var(--color-border))' }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>

      </div>

      {/* ── Positions Table Body ──────────────────────────────────────── */}
      <div className="bg-canvas border-x border-b border-border rounded-b-xl">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: '1120px', borderCollapse: 'separate', borderSpacing: 0 }}>
            <colgroup>
              {COLUMNS.map(col => <col key={col.key} style={{ width: col.width }} />)}
            </colgroup>
            <tbody>
              {liveGroups.map(group => {
                const isOpen = !collapsedGroups.has(group.key)
                const groupHoldingPct = portfolioValue > 0 ? (group.totalMktVal / portfolioValue) * 100 : 0

                return (
                  <Fragment key={group.key}>
                    {/* Group Header */}
                    <tr className="border-b border-border bg-canvas2/50 cursor-pointer hover:bg-canvas2 transition-colors" onClick={() => toggleGroup(group.key)}>
                      <td colSpan={colCount} className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {isOpen ? <ChevronDown size={14} className="text-inkMuted" /> : <ChevronRight size={14} className="text-inkMuted" />}
                          <span className="font-sans text-sm font-bold text-ink uppercase tracking-wide">{group.label}</span>
                          <span className="font-mono text-[10px] text-inkMuted">{group.positions.length} position{group.positions.length !== 1 ? 's' : ''}</span>
                        </div>
                      </td>
                    </tr>

                    {/* Position Rows */}
                    {isOpen && group.positions.map(pos => {
                      const isSymExp = pos.isMulti && !collapsedSymbols.has(pos.symbol)
                      const holdingPct = portfolioValue > 0 ? (pos.totalMktVal / portfolioValue) * 100 : 0

                      // Compute stop/risk for consolidated row
                      const isOpt = pos.instrumentType === 'OPTION'
                      const posStops = pos.accounts.map(a => {
                        const sm = stopMap[`${pos.symbol}|${a.accountNumber}`]
                        if (!sm) return null
                        const risk = Math.abs(a.avgCost - sm.stopPrice) * a.qty * (isOpt ? 100 : 1)
                        const acctEq = accountEquityMap[a.accountNumber] || 0
                        return { stopPrice: sm.stopPrice, riskDollars: risk, riskPct: acctEq > 0 ? (risk / acctEq) * 100 : 0, accountNumber: a.accountNumber }
                      })
                      const hasStop = posStops.some(s => s != null)
                      // For consolidated: use first stop price (should be same), sum risk
                      const firstStop = posStops.find(s => s != null)
                      const consolidatedStopPrice = firstStop?.stopPrice ?? null
                      const consolidatedRiskDollars = hasStop ? posStops.reduce((s, r) => s + (r?.riskDollars || 0), 0) : null
                      const consolidatedRiskPct = hasStop && totalEquity > 0 ? (consolidatedRiskDollars / totalEquity) * 100 : null
                      const perSharePrice = pos.price
                      const stopDistPct = consolidatedStopPrice != null && perSharePrice > 0
                        ? ((perSharePrice - consolidatedStopPrice) / perSharePrice) * 100
                        : null
                      const stopDistClr = stopDistPct == null ? '' : stopDistPct > 5 ? 'text-forest' : stopDistPct > 2 ? 'text-inkMuted' : 'text-ember'

                      return (
                        <Fragment key={pos.symbol}>
                          <tr
                            className="border-b border-border hover:bg-canvas2/30 transition-colors cursor-pointer"
                            onClick={(e) => handlePositionClick(pos.symbol, e)}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="w-4 flex-shrink-0 text-inkMuted"
                                  onClick={pos.isMulti ? (e) => { e.preventDefault(); e.stopPropagation(); toggleSymbol(pos.symbol) } : undefined}
                                >
                                  {pos.isMulti ? (isSymExp ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : null}
                                </span>
                                <div>
                                  <div className="font-mono text-sm font-semibold text-ink">{isOCCSymbol(pos.symbol) ? formatOCC(pos.symbol) : pos.symbol}</div>
                                  {pos.description && <div className="font-sans text-[11px] text-inkMuted leading-tight truncate max-w-[180px]">{pos.description}</div>}
                                  {!pos.isMulti && pos.accounts[0] && (
                                    <AccountName accountNumber={pos.accounts[0].accountNumber} className="font-mono text-[10px] text-inkMuted" />
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs text-ink">{mask(fmtQty(pos.totalQty))}</td>
                            <td className="px-4 py-3 text-right font-mono text-xs text-ink">{mask(fmtCurrency(pos.price))}</td>
                            <td className={`px-4 py-3 text-right font-mono text-xs tabular-nums leading-tight ${isExtendedSession ? (pos.priceChange >= 0 ? 'text-blue-400' : 'text-violet-400') : clr(pos.priceChange)}`}>
                              <div>{mask(fmtSigned(pos.priceChange))}</div>
                              <div className="text-[10px]">{mask(fmtPct(pos.priceChangePct))}</div>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs font-semibold tabular-nums text-ink">{mask(fmtCurrency(pos.totalMktVal))}</td>
                            <td className={`px-4 py-3 text-right font-mono text-xs font-semibold tabular-nums leading-tight ${isExtendedSession ? (pos.totalDayPnl >= 0 ? 'text-blue-400' : 'text-violet-400') : clr(pos.totalDayPnl)}`}>
                              <div>{mask(fmtSigned(pos.totalDayPnl))}</div>
                              <div className="text-[10px] font-normal">{mask(fmtPct(pos.dayChangePct))}</div>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs tabular-nums text-inkMuted">{mask(fmtCurrency(pos.totalCostBasis))}</td>
                            <td className={`px-4 py-3 text-right font-mono text-xs font-semibold tabular-nums leading-tight ${clr(pos.gainLoss)}`}>
                              <div>{mask(fmtSigned(pos.gainLoss))}</div>
                              <div className="text-[10px] font-normal">{mask(fmtPct(pos.gainLossPct))}</div>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs text-ink">
                              {consolidatedStopPrice != null ? mask(fmtCurrency(consolidatedStopPrice)) : '—'}
                            </td>
                            <td className={`px-4 py-3 text-right font-mono text-xs ${stopDistClr}`}>
                              {stopDistPct != null ? mask(stopDistPct.toFixed(1) + '%') : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs text-ember">
                              {consolidatedRiskDollars != null ? mask(fmtCurrency(consolidatedRiskDollars)) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs text-ember">
                              {consolidatedRiskPct != null ? mask(consolidatedRiskPct.toFixed(2) + '%') : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{mask(holdingPct.toFixed(1) + '%')}</td>
                          </tr>

                          {/* Per-account sub-rows */}
                          {isSymExp && pos.accounts.map(acct => {
                            const prevAcctVal = acct.mktVal - acct.dayPnl
                            const acctDayPct = prevAcctVal > 0 ? (acct.dayPnl / prevAcctVal) * 100 : 0
                            const acctStop = posStops.find(s => s?.accountNumber === acct.accountNumber)
                            const acctEq = accountEquityMap[acct.accountNumber] || 0
                            return (
                              <tr key={acct.accountNumber} className="border-b border-border bg-canvas2/20">
                                <td className="pl-12 pr-4 py-2">
                                  <AccountName accountNumber={acct.accountNumber} className="font-mono text-[11px] text-inkMuted" />
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-[11px] text-inkMuted">{mask(fmtQty(acct.qty))}</td>
                                <td className="px-4 py-2" />
                                <td className="px-4 py-2" />
                                <td className="px-4 py-2 text-right font-mono text-[11px] text-inkMuted">{mask(fmtCurrency(acct.mktVal))}</td>
                                <td className={`px-4 py-2 text-right font-mono text-[11px] ${isExtendedSession ? (acct.dayPnl >= 0 ? 'text-blue-400' : 'text-violet-400') : clr(acct.dayPnl)}`}>
                                  <div>{mask(fmtSigned(acct.dayPnl))}</div>
                                  <div className="text-[10px]">{mask(fmtPct(acctDayPct))}</div>
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-[11px] text-inkMuted">{mask(fmtCurrency(acct.costBasis))}</td>
                                <td className={`px-4 py-2 text-right font-mono text-[11px] ${clr(acct.gainLoss)}`}>
                                  <div>{mask(fmtSigned(acct.gainLoss))}</div>
                                  <div className="text-[10px]">{mask(fmtPct(acct.gainLossPct))}</div>
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-[11px] text-inkMuted">
                                  {acctStop ? mask(fmtCurrency(acctStop.stopPrice)) : '—'}
                                </td>
                                <td className="px-4 py-2" />
                                <td className="px-4 py-2 text-right font-mono text-[11px] text-ember">
                                  {acctStop ? mask(fmtCurrency(acctStop.riskDollars)) : '—'}
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-[11px] text-ember">
                                  {acctStop && acctEq > 0 ? mask((acctStop.riskDollars / acctEq * 100).toFixed(2) + '%') : '—'}
                                </td>
                                <td className="px-4 py-2" />
                              </tr>
                            )
                          })}
                        </Fragment>
                      )
                    })}

                    {/* Group Subtotal */}
                    {isOpen && (() => {
                      const groupRisk = group.positions.reduce((sum, p) => {
                        return sum + p.accounts.reduce((s2, a) => {
                          const sm = stopMap[`${p.symbol}|${a.accountNumber}`]
                          return s2 + (sm ? Math.abs(a.avgCost - sm.stopPrice) * a.qty : 0)
                        }, 0)
                      }, 0)
                      const groupRiskPct = groupRisk > 0 && totalEquity > 0 ? (groupRisk / totalEquity) * 100 : null
                      return (
                        <tr className="border-b border-border bg-canvas2/30" style={{ borderTop: '2px solid rgb(var(--color-border))' }}>
                          <td className="px-4 py-2.5 font-mono text-[10px] font-bold text-inkMuted uppercase tracking-wider" colSpan={4}>{group.label} Total</td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(group.totalMktVal))}</td>
                          <td className={`px-4 py-2.5 text-right font-mono text-xs font-bold ${isExtendedSession ? (group.totalDayPnl >= 0 ? 'text-blue-400' : 'text-violet-400') : clr(group.totalDayPnl)}`}>
                            <div>{mask(fmtSigned(group.totalDayPnl))}</div>
                            <div className="text-[10px] font-normal">{mask(fmtPct(group.dayChangePct))}</div>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(group.totalCostBasis))}</td>
                          <td className={`px-4 py-2.5 text-right font-mono text-xs font-bold ${clr(group.totalGainLoss)}`}>
                            <div>{mask(fmtSigned(group.totalGainLoss))}</div>
                            <div className="text-[10px] font-normal">{mask(fmtPct(group.gainLossPct))}</div>
                          </td>
                          <td className="px-4 py-2.5" />
                          <td className="px-4 py-2.5" />
                          <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-ember">
                            {groupRisk > 0 ? mask(fmtCurrency(groupRisk)) : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-ember">
                            {groupRiskPct != null ? mask(groupRiskPct.toFixed(2) + '%') : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-ink">{mask(groupHoldingPct.toFixed(1) + '%')}</td>
                        </tr>
                      )
                    })()}
                  </Fragment>
                )
              })}
            </tbody>

            {/* Grand Total Footer */}
            <tfoot>
              {(() => {
                const grandRisk = liveGroups.reduce((sum, g) => sum + g.positions.reduce((s2, p) =>
                  s2 + p.accounts.reduce((s3, a) => {
                    const sm = stopMap[`${p.symbol}|${a.accountNumber}`]
                    return s3 + (sm ? Math.abs(a.avgCost - sm.stopPrice) * a.qty : 0)
                  }, 0), 0), 0)
                const grandRiskPct = grandRisk > 0 && totalEquity > 0 ? (grandRisk / totalEquity) * 100 : null
                return (
                  <tr className="bg-canvas2" style={{ borderTop: '3px solid rgb(var(--color-border))' }}>
                    <td className="px-4 py-3 font-mono text-[10px] font-bold text-ink uppercase tracking-widest" colSpan={4}>Grand Total</td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(totalMktVal))}</td>
                    <td className={`px-4 py-3 text-right font-mono text-xs font-bold ${isExtendedSession ? (totalDayPnl >= 0 ? 'text-blue-400' : 'text-violet-400') : clr(totalDayPnl)}`}>
                      <div>{mask(fmtSigned(totalDayPnl))}</div>
                      <div className="text-[10px] font-normal">{mask(fmtPct(totalDayPct))}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(totalCostBasis))}</td>
                    <td className={`px-4 py-3 text-right font-mono text-xs font-bold ${clr(totalGainLoss)}`}>
                      <div>{mask(fmtSigned(totalGainLoss))}</div>
                      <div className="text-[10px] font-normal">{mask(fmtPct(totalGLPct))}</div>
                    </td>
                    <td className="px-4 py-3" />
                    <td className="px-4 py-3" />
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ember">
                      {grandRisk > 0 ? mask(fmtCurrency(grandRisk)) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ember">
                      {grandRiskPct != null ? mask(grandRiskPct.toFixed(2) + '%') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">100%</td>
                  </tr>
                )
              })()}
            </tfoot>
          </table>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          OPEN ORDERS
          ═══════════════════════════════════════════════════════════════════════ */}
      {ordersError ? (
        <div className="font-mono text-xs text-inkMuted px-1">Unable to load open orders.</div>
      ) : displayOrders.length > 0 ? (
        <div className="bg-canvas border border-border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Working</div>
            <h2 className="font-black text-base uppercase tracking-wide text-ink">Open Orders</h2>
          </div>
          <div className="px-6 py-4">
            {Object.entries(ordersByAccount).map(([acctNum, acctOrders]) => {
              const isAcctOpen = !collapsedOrderAccounts.has(acctNum)
              const acctEquity = accountEquityMap[acctNum] || 0
              return (
                <div key={acctNum} className="mb-4 last:mb-0">
                  {/* Account header */}
                  <div
                    className="flex items-center gap-2 cursor-pointer pb-2 mb-3"
                    style={{ borderBottom: '1px solid rgb(var(--color-border))' }}
                    onClick={() => toggleOrderAccount(acctNum)}
                  >
                    {isAcctOpen ? <ChevronDown size={14} className="text-inkMuted" /> : <ChevronRight size={14} className="text-inkMuted" />}
                    <AccountName accountNumber={acctNum} className="font-sans text-xs font-medium text-inkMuted uppercase" style={{ letterSpacing: '0.04em' }} />
                    <span className="font-mono text-[10px] text-inkMuted">· {acctOrders.length} order{acctOrders.length !== 1 ? 's' : ''}</span>
                  </div>
                  {isAcctOpen && acctOrders.map(order => {
                    const leg = order.orderLegCollection?.[0]
                    const symbol = leg?.instrument?.symbol || '—'
                    const instruction = leg?.instruction || '—'
                    const isSell = instruction.startsWith('SELL')
                    const entryPrice = order.entryPrice ?? (order.orderType === 'STOP' || order.orderType === 'STOP_LIMIT' ? order.stopPrice : order.price)
                    const stopPrice = order.computedStopPrice
                    const liveQ = livePrices[symbol]
                    const curPrice = liveQ?.last > 0 ? liveQ.last : currentPrices[symbol]
                    const qty = order.quantity || order.remainingQuantity

                    // Match standalone SELL STOP orders to existing positions
                    const isStandaloneStop = !order.hasChildren && isSell &&
                      (order.orderType === 'STOP' || order.orderType === 'STOP_LIMIT') && order.stopPrice != null
                    let matchedPos = null
                    if (isStandaloneStop && symbol !== '—') {
                      const pos = positions.find(p => p.symbol === symbol && p.account_number === acctNum)
                      if (pos) {
                        const avgEntry = Number(pos.average_price) || 0
                        const posQty = Number(pos.quantity) || 0
                        const posOptMult = pos.instrument_type === 'OPTION' ? 100 : 1
                        const posMaxLoss = Math.abs(avgEntry - order.stopPrice) * posQty * posOptMult
                        matchedPos = { avgEntry, quantity: posQty, maxLoss: posMaxLoss }
                      }
                    }

                    const maxLoss = order.bracketMaxLoss ?? (matchedPos?.maxLoss ?? null)
                    const pctPortfolio = maxLoss != null && acctEquity > 0 ? (maxLoss / acctEquity) * 100 : null
                    const enteredTime = order.enteredTime
                      ? new Date(order.enteredTime).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }) + ' ' + new Date(order.enteredTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
                      : '—'
                    const rawTif = typeof order.duration === 'string' ? order.duration : (order.duration?.durationType || '')
                    const TIF_MAP = { GOOD_TILL_CANCEL: 'GTC', DAY: 'DAY', FILL_OR_KILL: 'FOK', IMMEDIATE_OR_CANCEL: 'IOC' }
                    const tif = TIF_MAP[rawTif] || rawTif || '—'

                    // Child stop info for inline display on parent card
                    const childStop = order.hasChildren
                      ? order.childOrders?.find(c => (c.orderType === 'STOP' || c.orderType === 'STOP_LIMIT') && c.computedStopPrice != null)
                      : null

                    // Determine the display stop price: child stop for brackets, order.stopPrice for standalone stops
                    const displayStopPrice = stopPrice ?? (isStandaloneStop ? order.stopPrice : null)

                    // Status badge helper
                    const statusBadge = (status) => {
                      const map = {
                        WORKING: { label: 'WORKING', bg: 'rgba(106, 170, 94, 0.15)', color: 'rgb(var(--color-forest))' },
                        PENDING_ACTIVATION: { label: 'PENDING', bg: 'rgba(196, 149, 58, 0.15)', color: '#c4953a' },
                        AWAITING_PARENT_ORDER: { label: 'WAIT TRIGGER', bg: 'rgb(var(--color-canvas2))', color: 'rgb(var(--color-ink-muted))' },
                        QUEUED: { label: 'QUEUED', bg: 'rgba(91, 126, 161, 0.15)', color: '#5b7ea1' },
                        ACCEPTED: { label: 'ACCEPTED', bg: 'rgba(91, 126, 161, 0.15)', color: '#5b7ea1' },
                        NEW: { label: 'NEW', bg: 'rgba(91, 126, 161, 0.15)', color: '#5b7ea1' },
                      }
                      const m = map[status] || { label: status, bg: 'rgb(var(--color-canvas2))', color: 'rgb(var(--color-ink-muted))' }
                      return (
                        <span
                          className="font-mono font-semibold"
                          style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: m.bg, color: m.color }}
                        >
                          {m.label}
                        </span>
                      )
                    }

                    return (
                      <div
                        key={order.orderId}
                        style={{
                          background: 'rgb(var(--color-canvas))',
                          border: '1px solid rgb(var(--color-border))',
                          borderRadius: 8,
                          padding: '12px 16px',
                          marginBottom: 8,
                          ...(matchedPos ? { borderLeft: '2px solid #c4953a' } : {}),
                        }}
                      >
                        {/* Top line */}
                        <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
                          {/* Symbol */}
                          <span className="font-sans text-ink" style={{ fontSize: 15, fontWeight: 500 }}>{isOCCSymbol(symbol) ? formatOCC(symbol) : symbol}</span>
                          {/* Order type badge */}
                          <span
                            className="font-mono font-semibold"
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(91, 126, 161, 0.12)', color: 'rgb(var(--color-ink))' }}
                          >
                            {order.orderType === 'STOP_LIMIT' ? 'STOP LIMIT' : order.orderType}
                          </span>
                          {/* TIF badge */}
                          <span
                            className="font-mono"
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgb(var(--color-canvas2))', color: 'rgb(var(--color-ink-muted))' }}
                          >
                            {tif}
                          </span>
                          {/* POS STOP badge for standalone position stops */}
                          {matchedPos && (
                            <span
                              className="font-mono font-semibold"
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(196, 149, 58, 0.15)', color: '#c4953a' }}
                            >
                              POS STOP
                            </span>
                          )}
                          {/* Side + Qty */}
                          <span
                            className="font-mono"
                            style={{ fontSize: 12, fontWeight: 500, color: isSell ? 'rgb(var(--color-ember))' : 'rgb(var(--color-forest))' }}
                          >
                            {instruction.replace('_TO_OPEN', '').replace('_TO_CLOSE', '').replace('_SHORT', '')} {mask(String(qty))}
                          </span>
                          {/* Spacer */}
                          <span style={{ flex: 1 }} />
                          {/* Status badge */}
                          {statusBadge(order.status)}
                          {/* Date */}
                          <span className="font-mono" style={{ fontSize: 11, color: 'rgb(var(--color-ink-muted))' }}>{enteredTime}</span>
                        </div>

                        {/* Bottom line — data points */}
                        <div className="flex flex-wrap" style={{ gap: 24, marginTop: 10 }}>
                          {/* Entry */}
                          <span>
                            <span className="font-mono" style={{ fontSize: 11, color: 'rgb(var(--color-ink-muted))' }}>
                              {matchedPos ? 'Avg entry' : 'Entry'}
                            </span>
                            <span className="font-mono" style={{ fontSize: 13, color: 'rgb(var(--color-ink))', marginLeft: 6 }}>
                              {matchedPos
                                ? mask(fmtCurrency(matchedPos.avgEntry))
                                : entryPrice != null ? mask(fmtCurrency(entryPrice)) : '—'}
                            </span>
                          </span>
                          {/* Stop */}
                          <span>
                            <span className="font-mono" style={{ fontSize: 11, color: 'rgb(var(--color-ink-muted))' }}>Stop</span>
                            <span className="font-mono" style={{ fontSize: 13, color: 'rgb(var(--color-ink))', marginLeft: 6 }}>
                              {displayStopPrice != null ? mask(fmtCurrency(displayStopPrice)) : '—'}
                            </span>
                            {childStop && (
                              <span className="font-mono" style={{ fontSize: 11, color: 'rgb(var(--color-ink-muted))', marginLeft: 6 }}>
                                WAIT TRIGGER
                              </span>
                            )}
                          </span>
                          {/* Current */}
                          <span>
                            <span className="font-mono" style={{ fontSize: 11, color: 'rgb(var(--color-ink-muted))' }}>Current</span>
                            <span className="font-mono" style={{ fontSize: 13, color: 'rgb(var(--color-ink))', marginLeft: 6 }}>
                              {curPrice != null ? mask(fmtCurrency(curPrice)) : '—'}
                            </span>
                          </span>
                          {/* Max loss */}
                          <span>
                            <span className="font-mono" style={{ fontSize: 11, color: 'rgb(var(--color-ink-muted))' }}>Max loss</span>
                            <span className="font-mono" style={{ fontSize: 13, color: 'rgb(var(--color-ember))', marginLeft: 6 }}>
                              {maxLoss != null ? mask(fmtCurrency(maxLoss)) : '—'}
                            </span>
                            {pctPortfolio != null && (
                              <span className="font-mono" style={{ fontSize: 11, color: 'rgb(var(--color-ink-muted))', marginLeft: 4 }}>
                                ({mask(pctPortfolio.toFixed(2) + '%')})
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      ) : orders.length === 0 && !ordersError ? (
        <div className="bg-canvas border border-border rounded-xl px-6 py-8 text-center">
          <ClipboardList size={20} className="text-border mx-auto mb-2" strokeWidth={1.5} />
          <div className="font-mono text-xs text-inkMuted">No open orders</div>
        </div>
      ) : null}

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 1: CASH & MARGIN SUMMARY
          ═══════════════════════════════════════════════════════════════════════ */}
      <div className="bg-canvas border border-border rounded-xl">
        <div className="px-6 py-4 border-b border-border">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Buying Power</div>
          <h2 className="font-black text-base uppercase tracking-wide text-ink">Cash & Margin Summary</h2>
        </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border" style={{ background: 'rgb(var(--color-canvas))' }}>
                {[
                  'Account', 'Account Value', 'Cash', 'Cash %', 'Invested', 'Invested %',
                  ...(hasAnyMargin ? ['Buying Power', 'Avail. Funds', 'Maint. Req.'] : []),
                ].map(h => (
                  <th key={h} className={`px-4 py-3 font-mono text-[10px] tracking-widest uppercase text-inkMuted ${h === 'Account' ? 'text-left' : 'text-right'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cashData.map((row, i) => (
                <tr key={row.accountNumber} className={`border-b border-border last:border-0 hover:bg-canvas2/30 transition-colors ${i % 2 !== 0 ? 'bg-canvas2/20' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <AccountName accountNumber={row.accountNumber} className="font-mono text-sm font-semibold text-ink" />
                      {row.isIRA && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-canvas2 text-inkMuted border border-border">IRA</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-ink">{mask(fmtCurrency(row.value))}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(row.cash))}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{mask(row.cashPct.toFixed(1) + '%')}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(row.invested))}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{mask(row.investedPct.toFixed(1) + '%')}</td>
                  {hasAnyMargin && (
                    row.isIRA ? (
                      <>
                        <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">—</td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">—</td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">—</td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(row.buyingPower))}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(row.availableFunds))}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(row.maintenanceReq))}</td>
                      </>
                    )
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-canvas2" style={{ borderTop: '2px solid rgb(var(--color-border))' }}>
                <td className="px-4 py-3 font-mono text-[10px] font-bold text-inkMuted uppercase tracking-widest">
                  {cashData.length} account{cashData.length !== 1 ? 's' : ''}
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(cashTotals.value))}</td>
                <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(cashTotals.cash))}</td>
                <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(cashTotals.cashPct.toFixed(1) + '%')}</td>
                <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(cashTotals.invested))}</td>
                <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(cashTotals.investedPct.toFixed(1) + '%')}</td>
                {hasAnyMargin && (
                  <>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(cashTotals.buyingPower))}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(cashTotals.availableFunds))}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-ink">{mask(fmtCurrency(cashTotals.maintenanceReq))}</td>
                  </>
                )}
              </tr>
            </tfoot>
          </table>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 2: PORTFOLIO ALLOCATION CHARTS
          ═══════════════════════════════════════════════════════════════════════ */}
      <div>
        <div className="mb-4">
          <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-0.5">Analytics</div>
          <h2 className="font-black text-base uppercase tracking-wide text-ink">Portfolio Allocation</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Chart 1: Allocation by Instrument Type ──────────────────────── */}
          <div className="card">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Breakdown</div>
            <h3 className="font-black text-sm uppercase tracking-wide text-ink mb-4">By Instrument Type</h3>
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <DonutChart
                  data={typeChartData}
                  centerLabel={mask(fmtCompact(portfolioValue))}
                  centerSub="Portfolio"
                />
              </div>
              <div className="flex-1 min-w-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pr-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">Type</th>
                      <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Mkt Value</th>
                      <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">%</th>
                      <th className="py-2 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">#</th>
                    </tr>
                  </thead>
                  <tbody>
                    {typeChartData.map((d, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="py-2 pr-3 text-left">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                            <span className="font-sans text-xs text-ink">{d.name}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(d.value))}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(d.pct.toFixed(1) + '%')}</td>
                        <td className="py-2 text-right font-mono text-xs text-inkMuted">{d.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Chart 2: Top Holdings by Symbol ─────────────────────────────── */}
          <div className="card">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Concentration</div>
            <h3 className="font-black text-sm uppercase tracking-wide text-ink mb-4">Top Holdings</h3>
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <DonutChart
                  data={topHoldingsData}
                  centerLabel={`${allConsolidated.length}`}
                  centerSub="Positions"
                />
              </div>
              <div className="flex-1 min-w-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pr-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">Symbol</th>
                      <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Mkt Value</th>
                      <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">%</th>
                      <th className="py-2 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Accts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topHoldingsData.map((d, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="py-2 pr-3 text-left">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                            <span className="font-mono text-xs font-semibold text-ink">{d.name}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(d.value))}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(d.pct.toFixed(1) + '%')}</td>
                        <td className="py-2 text-right font-mono text-xs text-inkMuted">{d.acctCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Chart 3: Allocation by Account ──────────────────────────────── */}
          <div className="card">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Distribution</div>
            <h3 className="font-black text-sm uppercase tracking-wide text-ink mb-4">By Account</h3>
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <DonutChart
                  data={accountChartData}
                  centerLabel={mask(fmtCompact(cashTotals.value))}
                  centerSub={`${filteredAccounts.length} Acct${filteredAccounts.length !== 1 ? 's' : ''}`}
                />
              </div>
              <div className="flex-1 min-w-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pr-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">Account</th>
                      <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Value</th>
                      <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">%</th>
                      <th className="py-2 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Pos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountChartData.map((d, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="py-2 pr-3 text-left">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                            <span className="font-sans text-xs text-ink">{d.name}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(d.value))}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(d.pct.toFixed(1) + '%')}</td>
                        <td className="py-2 text-right font-mono text-xs text-inkMuted">{d.posCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Chart 4: Concentration Risk ─────────────────────────────────── */}
          <div className="card">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Risk</div>
            <h3 className="font-black text-sm uppercase tracking-wide text-ink mb-4">Concentration Risk</h3>
            {concentrationData.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(concentrationData.length * 28 + 40, 120)}>
                <BarChart
                  data={concentrationData}
                  layout="vertical"
                  margin={{ top: 5, right: 50, left: 5, bottom: 5 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, Math.max(...concentrationData.map(d => d.pct), 12)]}
                    tick={{ fill: axisColor, fontSize: 10, fontFamily: '"DM Sans", sans-serif' }}
                    tickFormatter={v => v.toFixed(0) + '%'}
                    axisLine={{ stroke: 'rgb(var(--color-border))' }}
                    tickLine={{ stroke: 'rgb(var(--color-border))' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="symbol"
                    width={55}
                    tick={{ fill: axisColor, fontSize: 11, fontFamily: '"DM Sans", sans-serif' }}
                    axisLine={{ stroke: 'rgb(var(--color-border))' }}
                    tickLine={false}
                  />
                  <ReferenceLine
                    x={10}
                    stroke={chartEmber}
                    strokeDasharray="4 4"
                    label={{ value: '10%', position: 'top', fill: chartEmber, fontSize: 10, fontFamily: '"DM Sans", sans-serif' }}
                  />
                  <Tooltip content={<ConcentrationTooltip />} />
                  <Bar dataKey="pct" radius={[0, 3, 3, 0]} barSize={16}>
                    {concentrationData.map((entry, i) => (
                      <Cell key={i} fill={entry.pct > 10 ? chartEmber : chartGreen} />
                    ))}
                    <LabelList
                      dataKey="pctLabel"
                      position="right"
                      style={{ fill: axisColor, fontSize: 10, fontFamily: '"DM Sans", sans-serif' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 font-mono text-xs text-inkMuted">No position data</div>
            )}
          </div>

          {/* ── Chart 5: Sector Allocation ───────────────────────────────── */}
          <div className="card">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Diversification</div>
            <h3 className="font-black text-sm uppercase tracking-wide text-ink mb-4">Sector Allocation</h3>
            {sectorChartData.length > 0 ? (
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="flex-shrink-0">
                  <DonutChart
                    data={sectorChartData}
                    centerLabel={`${sectorChartData.length}`}
                    centerSub="Sectors"
                  />
                </div>
                <div className="flex-1 min-w-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 pr-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">Sector</th>
                        <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Mkt Value</th>
                        <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">%</th>
                        <th className="py-2 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">#</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sectorChartData.map((d, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="py-2 pr-3 text-left">
                            <div className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                              <span className="font-sans text-xs text-ink">{d.name}</span>
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(d.value))}</td>
                          <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(d.pct.toFixed(1) + '%')}</td>
                          <td className="py-2 text-right font-mono text-xs text-inkMuted">{d.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 font-mono text-xs text-inkMuted">No position data</div>
            )}
          </div>

          {/* ── Chart 6: Cash vs Invested ───────────────────────────────────── */}
          <div className="card">
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">Liquidity</div>
            <h3 className="font-black text-sm uppercase tracking-wide text-ink mb-4">Cash vs Invested</h3>
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0">
                <DonutChart
                  data={cashVsInvestedData}
                  centerLabel={mask(fmtCompact(cashTotals.value))}
                  centerSub="Total"
                />
              </div>
              <div className="flex-1 min-w-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pr-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">Category</th>
                      <th className="py-2 pr-3 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">Amount</th>
                      <th className="py-2 text-right font-mono text-[10px] tracking-widest uppercase text-inkMuted">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashVsInvestedData.map((d, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="py-2 pr-3 text-left">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                            <span className="font-sans text-xs text-ink">{d.name}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right font-mono text-xs text-inkMuted">{mask(fmtCurrency(d.value))}</td>
                        <td className="py-2 text-right font-mono text-xs text-inkMuted">{mask(d.pct.toFixed(1) + '%')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Concentration Risk Tooltip ────────────────────────────────────────────────
function ConcentrationTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="bg-canvas border border-border rounded-lg px-3 py-2 shadow-lg">
      <div className="font-mono text-xs font-semibold text-ink">{d.symbol}</div>
      <div className="font-mono text-xs text-inkMuted">{d.pct.toFixed(2)}% of portfolio</div>
    </div>
  )
}
